import React, { useCallback, useMemo, useRef, useState } from 'react';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey, getImageName } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { ButtonWithGAClickButton } from '../button';
import { GalleryModal, GalleryModalImage } from '../gallery-modal';
import { Section } from '../section';
import { Swiper, SwiperSlide } from '../swiper';
import {
  gridGallery,
  gridGalleryImage,
  gridGallerySlide,
  GalleryImageWrapper,
  inspirationGalleryDescription,
  inspirationGalleryTitle,
} from './inspiration-gallery-styles';
import { Typography } from '../typography';
import { SectionSubTitle } from '../section-subtitle';

type InspirationGalleryImage = GalleryModalImage & { gridSrc: string };

type InspirationGalleryProps = {
  description: string;
  elementBlock: string;
  elementName: string;
  imageList: InspirationGalleryImage[];
  title: string;
  signHref: string;
};

export const InspirationGallery: React.FC<InspirationGalleryProps> = ({
  elementBlock,
  elementName,
  imageList,
  signHref,
  title,
  description,
}) => {
  const bannerRefs = useRef<HTMLElement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const promotions = useMemo(
    () =>
      imageList.map((image, index: number) => ({
        name: elementName,
        creative: getImageName(image.src),
        position: (index + 1).toString(),
      })),
    [elementName, imageList]
  );

  usePromoViewEvent(bannerRefs, `${elementBlock} / gallery`, promotions);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, [setIsModalOpen, setActiveImageIndex]);

  const handleImageClick = useCallback(
    (index: number) => () => {
      setIsModalOpen(true);
      setActiveImageIndex(index);
      analyticsUtils.promoClickEvent(`${elementBlock} / gallery`, promotions[index]);
    },
    [elementBlock, promotions]
  );

  return (
    <Section>
      <SectionSubTitle className={inspirationGalleryTitle} margin="s">{title}</SectionSubTitle>
      <Typography className={inspirationGalleryDescription} size="body_long" variant="l">{description}</Typography>
      <Swiper
        className={gridGallery}
        slidesPerView="auto"
        watchOverflow
        breakpoints={{
          768: {
            noSwiping: true,
            noSwipingSelector: '.swiper',
          },
        }}>
        {imageList.map((image: InspirationGalleryImage, index: number) => (
          <SwiperSlide className={gridGallerySlide} key={createElementKey('grid-gallery', index)}>
            <GalleryImageWrapper
              data-testid="gallery-image"
              data-position={index}
              onClick={handleImageClick(index)}
              ref={(refIndex: number) => (el: HTMLElement | null) => {
                if (el && bannerRefs) bannerRefs.current[refIndex] = el;
              }}
            >
              <img
                alt={image.alt}
                className={gridGalleryImage}
                loading="lazy"
                src={image.gridSrc}
              />
            </GalleryImageWrapper>
          </SwiperSlide>
        ))}
      </Swiper>

      <GalleryModal
        clickedImageIndex={activeImageIndex}
        elementBlock={elementBlock}
        elementText={`${elementName} / modal`}
        galleryList={imageList}
        onClose={handleCloseModal}
        renderFooterButton={() => (
          <ButtonWithGAClickButton
            elementBlock={elementBlock}
            elementName={`${elementName} / modal`}
            elementText="Записаться на проектирование"
            invert
            variant="accent"
            href={signHref}
            target="_blank"
            onClick={handleCloseModal}>
            Записаться на проектирование
          </ButtonWithGAClickButton>
        )}
        show={isModalOpen}
      />
    </Section>
  );
};
